// =====================================================================
// Camada semântica do agente (PORTAL_DESIGN §10.2). Dicionário de dados que
// descreve as VIEWS SEGURAS (report.*/meta.*), o significado das colunas e os
// sinônimos pt-BR. É injetado no prompt do planejador para gerar SQL só-leitura
// correto e citável. NÃO inclui dim/raw cru (PII) por padrão.
// =====================================================================

export interface SemanticColumn {
  name: string;
  desc: string;
}
export interface SemanticView {
  view: string; // ex.: report.v_operacao_vega
  desc: string;
  columns: SemanticColumn[];
}

export const SEMANTIC_VIEWS: SemanticView[] = [
  {
    view: "report.v_viagem_vega",
    desc: "Operação VEGA tipada (uma linha por viagem). Base para frete, metas e atrasos.",
    columns: [
      { name: "competencia", desc: "mês de referência (1º dia do mês)" },
      { name: "numero_ordem", desc: "número da ordem (chave da programação)" },
      { name: "motorista", desc: "nome do motorista" },
      { name: "cavalo", desc: "placa do cavalo (trator)" },
      { name: "cidade_entrega", desc: "cidade de destino da carga" },
      { name: "regiao_meta", desc: "região usada para meta (ex.: 02_REGIÃO_CURITIBA)" },
      { name: "status_carga", desc: "status: LIBERADO, AGENDADO ou CARREGADO" },
      { name: "peso", desc: "peso em toneladas" },
      { name: "tarifa", desc: "tarifa unitária calculada" },
      { name: "valor_total_frete", desc: "valor total do frete da viagem (receita)" },
      { name: "atraso_coleta", desc: "Atraso ou OK" },
      { name: "pbtc_flag", desc: "PBTC (excesso de peso) ou ✔" },
    ],
  },
  {
    view: "report.mv_metas",
    desc: "Metas agregadas por competência × região × status (nº de cargas, peso, frete).",
    columns: [
      { name: "competencia", desc: "mês de referência" },
      { name: "regiao", desc: "região da meta" },
      { name: "status", desc: "LIBERADO/AGENDADO/CARREGADO/OUTROS" },
      { name: "n_cargas", desc: "quantidade de cargas" },
      { name: "peso_total", desc: "soma do peso (t)" },
      { name: "frete_total", desc: "soma do valor do frete" },
    ],
  },
  {
    view: "report.v_insight_frete",
    desc: "Faturamento de frete por competência × região (total e médio).",
    columns: [
      { name: "competencia", desc: "mês" },
      { name: "regiao", desc: "região" },
      { name: "n_viagens", desc: "nº de viagens" },
      { name: "frete_total", desc: "receita total de frete" },
      { name: "frete_medio", desc: "ticket médio de frete" },
    ],
  },
  {
    view: "report.v_compliance_vencida",
    desc: "Motoristas com curso ou exame VENCIDO (validade no passado).",
    columns: [
      { name: "tipo", desc: "CURSO AUTOR ou EXAME PERIODICO" },
      { name: "nome", desc: "nome do motorista" },
      { name: "validade", desc: "data de validade (já vencida)" },
      { name: "dias_faltantes", desc: "negativo = dias desde o vencimento" },
    ],
  },
  {
    view: "report.v_frota",
    desc: "Mapa motorista -> veículos (cavalo e carretas).",
    columns: [
      { name: "motorista", desc: "nome do motorista" },
      { name: "cavalo", desc: "placa do cavalo" },
      { name: "primeira_carreta", desc: "placa da 1ª carreta" },
      { name: "status", desc: "status do veículo" },
    ],
  },
  {
    view: "report.v_inconsistencias",
    desc: "Inconsistências catalogadas (erros de fórmula, duplicados, compliance vencida...).",
    columns: [
      { name: "kind", desc: "tipo da inconsistência" },
      { name: "severity", desc: "info/warning/error" },
      { name: "sheet", desc: "aba de origem" },
      { name: "detail", desc: "detalhe (jsonb)" },
    ],
  },
];

// Sinônimos pt-BR -> termo canônico (ajuda o planejador a mapear a pergunta).
export const SYNONYMS: Record<string, string> = {
  cavalo: "placa do cavalo (trator)",
  frete: "valor_total_frete",
  faturamento: "valor_total_frete",
  receita: "valor_total_frete",
  carga: "viagem",
  meta: "report.mv_metas",
  vencido: "report.v_compliance_vencida",
  região: "regiao_meta",
  competência: "competencia (mês de referência)",
  exame: "EXAME PERIODICO",
  curso: "CURSO AUTOR",
};

/** Renderiza o dicionário como texto para o prompt do planejador LLM. */
export function renderSemanticLayer(): string {
  const lines: string[] = [
    "VIEWS DISPONÍVEIS (somente leitura, schema report/meta):",
  ];
  for (const v of SEMANTIC_VIEWS) {
    lines.push(`\n• ${v.view} — ${v.desc}`);
    for (const c of v.columns) lines.push(`    - ${c.name}: ${c.desc}`);
  }
  lines.push("\nSINÔNIMOS pt-BR:");
  for (const [k, val] of Object.entries(SYNONYMS)) lines.push(`    - "${k}" => ${val}`);
  return lines.join("\n");
}

// Allowlist de objetos consultáveis pelo agente (defesa adicional além do role).
export const AGENT_ALLOWED_RELATIONS = new Set(SEMANTIC_VIEWS.map((v) => v.view));
